/// <reference path="../../env.d.ts" />
import type { Router } from "vue-router";
import { createGlobalState } from "@vueuse/core";
import { computed, ref } from "vue";

export enum UpdateReason {
  SERVICE_WORKER = "service_worker",
  CHUNK_LOAD_ERROR = "chunk_load_error",
}

export const COUNTDOWN_DURATION = 10;

export interface RegisterSWModule {
  useRegisterSW: (options: {
    immediate?: boolean
    onNeedRefresh?: () => void
    onRegistered?: (registration: ServiceWorkerRegistration | undefined) => void
  }) => {
    updateServiceWorker: (reloadPage?: boolean) => Promise<void>
  }
}

export type LoadRegisterSW = () => Promise<RegisterSWModule>;

export const useAppUpdate = createGlobalState(() => {
  const MAX_DISMISSALS = 3;
  const updateAvailable = ref(false);
  const updateReason = ref<UpdateReason>(UpdateReason.SERVICE_WORKER);
  const dismissCount = ref(0);
  const canDismiss = computed(() => dismissCount.value < MAX_DISMISSALS);
  const isApplying = ref(false);
  const remainingSeconds = ref(COUNTDOWN_DURATION);
  let doUpdateServiceWorker: (() => Promise<void>) | null = null;
  let countdownInterval: ReturnType<typeof setInterval> | null = null;

  function isChunkLoadError(error: unknown): boolean {
    if (error instanceof Error) {
      return (
        error.message.includes("Failed to fetch dynamically imported module")
        || error.message.includes("Importing a module script failed")
        || error.message.includes("error loading dynamically imported module")
        || error.name === "ChunkLoadError"
      );
    }
    return false;
  }

  function clearCountdown() {
    if (countdownInterval) {
      clearInterval(countdownInterval);
      countdownInterval = null;
    }
  }

  function startCountdown() {
    clearCountdown();
    remainingSeconds.value = COUNTDOWN_DURATION;

    countdownInterval = setInterval(() => {
      remainingSeconds.value--;

      if (remainingSeconds.value <= 0) {
        clearCountdown();
        applyUpdate();
      }
    }, 1000);
  }

  function showUpdate(reason: UpdateReason) {
    updateReason.value = reason;
    updateAvailable.value = true;
    startCountdown();
  }

  async function applyUpdate() {
    clearCountdown();
    isApplying.value = true;
    if (doUpdateServiceWorker) {
      await doUpdateServiceWorker();
      return;
    }
    window.location.reload();
  }

  function dismissUpdate() {
    if (updateReason.value === UpdateReason.CHUNK_LOAD_ERROR) {
      return;
    }
    if (!canDismiss.value) {
      return;
    }
    clearCountdown();
    dismissCount.value++;
    updateAvailable.value = false;
  }

  function initServiceWorker(loadRegisterSW: LoadRegisterSW) {
    if (!import.meta.env.PROD) {
      return;
    }

    loadRegisterSW().then(({ useRegisterSW }) => {
      const { updateServiceWorker } = useRegisterSW({
        immediate: true,
        onNeedRefresh() {
          doUpdateServiceWorker = () => updateServiceWorker(true);
          showUpdate(UpdateReason.SERVICE_WORKER);
        },
        onRegistered(r: ServiceWorkerRegistration | undefined) {
          if (!r) {
            return;
          }
          setInterval(async () => {
            await r.update();
          }, 60 * 1000);
        },
      });
    });
  }

  function initChunkErrorHandler(router: Router) {
    window.addEventListener("vite:preloadError", () => {
      showUpdate(UpdateReason.CHUNK_LOAD_ERROR);
    });

    router.onError((error) => {
      if (isChunkLoadError(error)) {
        showUpdate(UpdateReason.CHUNK_LOAD_ERROR);
      }
    });
  }

  function init(router: Router, loadRegisterSW?: LoadRegisterSW) {
    if (loadRegisterSW) {
      initServiceWorker(loadRegisterSW);
    }
    initChunkErrorHandler(router);

    if (import.meta.env.DEV) {
      (window as any).__APP_UPDATE__ = {
        showSwUpdate: () => showUpdate(UpdateReason.SERVICE_WORKER),
        showChunkError: () => showUpdate(UpdateReason.CHUNK_LOAD_ERROR),
        dismiss: () => dismissUpdate(),
      };
    }
  }

  return {
    updateAvailable,
    updateReason,
    canDismiss,
    isApplying,
    remainingSeconds,
    applyUpdate,
    dismissUpdate,
    init,
  };
});
